import {
    AutomationExecution,
    ExecutionStatus,
    NodeType
} from 'crqlar-react-common/dist/models/automation/automation';

import {isFanOutNode} from '../config/nodeCategories';
import {runGuestName, teamEmailOutcome} from '../executions/executionFormat.util';
import {isTerminal, ownSteps, parseStepResult, queuedChildren} from './executionSteps';

// Built only from what the engine stored; a step is stored once finished, so nothing guesses the running step.
// running: queued, running, waiting for member runs, a Wait whose time has come, or member runs not loaded yet.
// parked: every unfinished run sits at a timed Wait ending after `now` (± CLOCK_SKEW_MS). Else every run settled.

export type TestRunPhase = 'running' | 'parked' | 'finished' | 'failed' | 'cancelled';

/** As the trigger dialog started the test; `address` receives its emails. */
export interface TestRunSubject {
    address: string;
    /** The people ticked under "Test with"; absent when the flow offered no choice. */
    memberCount?: number;
    /** The one ticked person's name. */
    memberName?: string;
    /** Team members rather than guests. */
    team?: boolean;
}

export type TrailMark = 'passed' | 'failed';

/** Finished with emails in the inbox: the strip, the canvas tint and the passed outlines turn green. */
export const testRunSucceeded = (state: TestRunState): boolean => state.phase === 'finished' && state.emails > 0;

export type FailureReason = 'no-recipients' | 'no-survey' | 'no-audience' | 'audience-gone' | 'other';

/** Why a finished test put nothing in the inbox, named from the stored steps. */
export type NothingSent =
    | {kind: 'no-recipient'; step: string}
    | {kind: 'no-address'; name?: string; count: number}
    | {kind: 'condition'; name?: string; condition: string}
    | {kind: 'left-audience'; name?: string; count: number; audience: string}
    | {kind: 'empty-audience'; audience: string}
    | {kind: 'push-only'}
    | {kind: 'none'};

export interface TestRunState {
    phase: TestRunPhase;
    /** Every step the test reached, in any of its runs; a step that failed in one of them is `failed`. */
    trail: ReadonlyMap<string, TrailMark>;
    /** Emails queued to the tester, over every run. */
    emails: number;
    /** People an email step skipped because they have no address. */
    skipped: number;
    /** The test's own member runs; 0 when it did not fan out. */
    memberRuns: number;
    /** The tester skipped a Wait: the steps below it were judged by today's data. */
    waitSkipped: boolean;
    parked?: {waitName: string; until: Date};
    /** `failedRuns`: member runs with a failure (a deeper run counts for its member run); 0 when the test's own run failed. */
    failure?: {stepName?: string; reason: FailureReason; failedRuns: number};
    nothingSent?: NothingSent;
    /** The run View run opens: the member run that failed, else the test's own. */
    viewExecutionId?: string;
}

const listOf = (value: unknown): Record<string, any>[] => (Array.isArray(value) ? value : []);

// A wait ending this close to `now` counts as over: Skip the wait sets `waitUntil` by the server's clock,
// and a browser clock running behind would read the run as parked again.
const CLOCK_SKEW_MS = 30 * 1000;

const timeOf = (value?: Date | string | null): number | undefined => {
    if (!value) return undefined;
    const time = (value instanceof Date ? value : new Date(value)).getTime();
    return Number.isNaN(time) ? undefined : time;
};

// A parent waiting for its member runs is WAITING too, but has no `waitUntil`.
const atTimedWait = (execution: AutomationExecution) => (
    execution.status === ExecutionStatus.WAITING && timeOf(execution.waitUntil) !== undefined
);

const waitingForMembers = (execution: AutomationExecution) => (
    execution.status === ExecutionStatus.WAITING && timeOf(execution.waitUntil) === undefined
);

/** Matched on the API's own messages (executors, `NoRecipientsError`); they are English and stable. */
export const failureReason = (error?: string): FailureReason => {
    const text = (error ?? '').trim();
    if (text.startsWith('No recipients found')) return 'no-recipients';
    if (text.startsWith('No survey is selected')) return 'no-survey';
    if (text.startsWith('No audience selected')) return 'no-audience';
    if (text.startsWith('Audience no longer exists')) return 'audience-gone';
    return 'other';
};

/** `executions`: the test's own run first, then its member runs (any depth); undefined before the first answer. */
export const testRunState = (
    executions: AutomationExecution[] | undefined,
    subject: TestRunSubject,
    now: number
): TestRunState => {
    const runs = executions ?? [];
    const root = runs[0];

    const trail = new Map<string, TrailMark>();
    runs.forEach(run => (run.executionSteps ?? []).forEach(step => {
        if (step.error) trail.set(step.nodeId, 'failed');
        else if (!trail.has(step.nodeId)) trail.set(step.nodeId, 'passed');
    }));

    let emails = 0;
    let skipped = 0;
    let noRecipientStep: string | undefined;
    let emailSteps = 0;
    let pushesHeld = 0;
    let waitSkipped = false;

    // Own steps only: a member run starts with a copy of its parent's steps.
    runs.forEach(run => ownSteps(run).forEach(step => {
        const result = parseStepResult(step.result);

        if (step.nodeType === NodeType.SEND_EMAIL) {
            emailSteps++;
            // The API skips a step with nobody filled in (NO_RECIPIENT_CONFIGURED) as well as guests without an address.
            if (result.skipped === true && result.reason === 'NO_RECIPIENT_CONFIGURED') {
                if (noRecipientStep === undefined) noRecipientStep = step.nodeName || '';
            } else if (result.skipped === true) skipped++;
            emails += listOf(result.messages).filter(message => message.status !== 'failed').length;
        }
        // One email per member run, to the tester in a test run.
        if (step.nodeType === NodeType.SEND_TEAM_EMAIL) {
            emailSteps++;
            if (teamEmailOutcome(result) === 'sent') emails++;
        }
        if (step.nodeType === NodeType.SEND_PUSH && result.held) {
            pushesHeld += listOf(result.notifications).length || Number(result.totalRecipients) || 1;
        }
        if (step.nodeType === NodeType.WAIT && result.skippedByTester === true) waitSkipped = true;
    }));

    const memberRunsOf = (parent: AutomationExecution) => runs.filter(run => !!parent.id && run.parentExecutionId === parent.id);
    const memberRuns = root ? memberRunsOf(root).length : 0;
    // A fan-out queued more member runs than the last read returned: still on their way.
    const missing = runs.some(run => queuedChildren(run) > memberRunsOf(run).length);

    const state: TestRunState = {phase: 'running', trail, emails, skipped, memberRuns, waitSkipped, viewExecutionId: root?.id};
    if (!root) return state;

    const nothingSent = (): NothingSent => {
        // Named only when it is one person: the single member run, or an event-started run's guest.
        const person = memberRuns === 1
            ? runGuestName(memberRunsOf(root)[0]) ?? subject.memberName
            : memberRuns === 0 ? runGuestName(root) ?? subject.memberName : undefined;

        if (noRecipientStep !== undefined) return {kind: 'no-recipient', step: noRecipientStep};
        if (skipped) return {kind: 'no-address', name: person, count: skipped};

        const stopped = runs.flatMap(run => ownSteps(run)).find(step => (
            step.nodeType === NodeType.CONTINUE_IF && parseStepResult(step.result).evaluated === false
        ));
        if (stopped) return {kind: 'condition', name: person, condition: stopped.nodeName || ''};

        const emptyFanOut = ownSteps(root).find(step => (
            isFanOutNode(step.nodeType) && !(Number(parseStepResult(step.result).queued) > 0)
        ));
        if (emptyFanOut) {
            const audience = emptyFanOut.nodeName || '';
            return subject.memberCount
                ? {kind: 'left-audience', name: subject.memberName, count: subject.memberCount, audience}
                : {kind: 'empty-audience', audience};
        }

        if (!emailSteps && pushesHeld) return {kind: 'push-only'};
        return {kind: 'none'};
    };

    const unfinished = runs.filter(run => !isTerminal(run));

    if (!missing && unfinished.length === 0) {
        const failed = runs.filter(run => run.status === ExecutionStatus.FAILED);
        if (failed.length) {
            const failedRun = failed.find(run => ownSteps(run).some(step => step.error)) ?? failed[0];
            const errorStep = ownSteps(failedRun).find(step => step.error);

            // Counted per member run of the test, so "For 1 of 2 guests" never exceeds the guests.
            const byId = new Map(runs.map(run => [run.id, run] as const));
            const memberRunOf = (run: AutomationExecution): AutomationExecution => {
                let current = run;
                for (let depth = 0; depth < runs.length; depth++) {
                    const parentId = current.parentExecutionId;
                    const parent = parentId && parentId !== root.id ? byId.get(parentId) : undefined;
                    if (!parent) break;
                    current = parent;
                }
                return current;
            };
            const failedMembers = new Set(failed.filter(run => run !== root).map(run => memberRunOf(run).id));

            return {
                ...state,
                phase: 'failed',
                failure: {
                    stepName: errorStep?.nodeName || undefined,
                    reason: failureReason(errorStep?.error ?? failedRun.errorMessage),
                    failedRuns: failedRun === root ? 0 : failedMembers.size
                },
                viewExecutionId: failedRun.id
            };
        }

        if (runs.some(run => run.status === ExecutionStatus.CANCELLED)) return {...state, phase: 'cancelled'};

        return {...state, phase: 'finished', nothingSent: emails ? undefined : nothingSent()};
    }

    const futureWaits = unfinished.filter(run => atTimedWait(run) && timeOf(run.waitUntil)! > now + CLOCK_SKEW_MS);
    const parked = !missing && futureWaits.length > 0
        && unfinished.every(run => atTimedWait(run) || waitingForMembers(run));

    if (parked) {
        const first = futureWaits.reduce((soonest, run) => (
            timeOf(run.waitUntil)! < timeOf(soonest.waitUntil)! ? run : soonest
        ));
        const waitStep = (first.executionSteps ?? []).find(step => step.nodeId === first.waitNodeId)
            ?? [...(first.executionSteps ?? [])].reverse().find(step => step.nodeType === NodeType.WAIT);

        return {
            ...state,
            phase: 'parked',
            parked: {waitName: waitStep?.nodeName ?? '', until: new Date(timeOf(first.waitUntil)!)}
        };
    }

    return state;
};
